import React, { useMemo } from "react";
import { NavigateFunction } from "react-router-dom";
import { Badge } from "@altimateai/lego";
import { Stack } from "@altimateai/lego";
import { EvalResult, TurnResult, ActiveRun } from "../../services/evals";
import { TurnResultCard } from "./TurnResultCard";
import { formatDateTime } from "../utils";

interface BatchRunRowProps {
  runName: string;
  results: EvalResult[];
  activeRun: ActiveRun | null;
  expandedBatchRun: string | null;
  expandedBatchRunResultId: string | null;
  setExpandedBatchRun: (id: string | null) => void;
  setExpandedBatchRunResultId: (id: string | null) => void;
  navigate: NavigateFunction;
}

export const BatchRunRow: React.FC<BatchRunRowProps> = ({
  runName,
  results,
  activeRun,
  expandedBatchRun,
  expandedBatchRunResultId,
  setExpandedBatchRun,
  setExpandedBatchRunResultId,
  navigate,
}) => {
  // Memoize expensive calculations to prevent re-computation on every render
  const stats = useMemo(() => {
    const total = results.length;
    const successful = results.filter(r => Math.round(r.overall_score) >= 4).length;
    const failed = total - successful;
    const avgScore = results.reduce((sum, r) => sum + r.overall_score, 0) / total;
    const executedAt = results[0].created_at;

    return { total, successful, failed, avgScore, executedAt };
  }, [results]);

  const { total, successful, failed, avgScore, executedAt } = stats;

  const isActiveRun =
    activeRun && activeRun.name === runName && activeRun.completed < activeRun.total;
  const isExpanded = expandedBatchRun === runName;

  return (
    <React.Fragment>
      <tr
        className={`hover:al-bg-gray-50 al-cursor-pointer ${isExpanded ? "al-bg-blue-50" : ""}`}
        onClick={() => {
          const newExpandedRun = isExpanded ? null : runName;
          setExpandedBatchRun(newExpandedRun);
          if (newExpandedRun) {
            navigate(`?tab=runs&batch_run=${encodeURIComponent(runName)}`, { replace: true });
          } else {
            navigate(`?tab=runs`, { replace: true });
          }
        }}
      >
        <td className="al-px-4 al-py-3 al-text-[13px] al-text-gray-900 al-font-medium al-break-words">
          <Stack direction="row" align="center" gap="sm">
            <span className="al-text-gray-400 al-flex-shrink-0">{isExpanded ? "▼" : "▶"}</span>
            <span className="al-break-words al-overflow-hidden">{runName}</span>
          </Stack>
        </td>
        <td className="al-px-4 al-py-3 al-text-[13px]">
          {isActiveRun ? (
            <Badge className="!al-bg-blue-100 !al-text-blue-700 al-border al-border-blue-200">
              Running
            </Badge>
          ) : (
            <Badge className="!al-bg-gray-100 !al-text-gray-700 al-border al-border-gray-200">
              Completed
            </Badge>
          )}
        </td>
        <td className="al-px-4 al-py-3 al-text-[13px] al-text-gray-600">{total}</td>
        <td className="al-px-4 al-py-3 al-text-[13px]">
          <span className="al-text-green-600 al-font-semibold">{successful}</span>
        </td>
        <td className="al-px-4 al-py-3 al-text-[13px]">
          <span className="al-text-red-600 al-font-semibold">{failed}</span>
        </td>
        <td className="al-px-4 al-py-3 al-text-[13px]">
          <Badge
            className={
              avgScore >= 4
                ? "!al-bg-green-100 !al-text-green-700 al-border al-border-green-200"
                : avgScore >= 3
                  ? "!al-bg-yellow-100 !al-text-yellow-700 al-border al-border-yellow-200"
                  : "!al-bg-red-100 !al-text-red-700 al-border al-border-red-200"
            }
          >
            {avgScore.toFixed(1)}/5
          </Badge>
        </td>
        <td className="al-px-4 al-py-3 al-text-[13px] al-text-gray-600">
          {formatDateTime(executedAt)}
        </td>
      </tr>
      {isExpanded && (
        <tr>
          <td colSpan={7} className="al-p-0">
            <div className="al-bg-gray-50 al-border-t al-border-b al-border-gray-200 al-max-h-[70vh] al-overflow-y-auto al-overflow-x-hidden">
              <div className="al-p-6 al-max-w-full">
                {/* Summary stats */}
                <div className="al-grid al-grid-cols-4 al-gap-4 al-mb-6">
                  <div className="al-text-center al-p-4 al-bg-white al-rounded-lg al-shadow-sm">
                    <div className="al-text-2xl al-font-bold">{total}</div>
                    <div className="al-text-sm al-text-gray-600">Total Tests</div>
                  </div>
                  <div className="al-text-center al-p-4 al-bg-white al-rounded-lg al-shadow-sm">
                    <div className="al-text-2xl al-font-bold al-text-green-600">{successful}</div>
                    <div className="al-text-sm al-text-gray-600">Passed</div>
                  </div>
                  <div className="al-text-center al-p-4 al-bg-white al-rounded-lg al-shadow-sm">
                    <div className="al-text-2xl al-font-bold al-text-red-600">{failed}</div>
                    <div className="al-text-sm al-text-gray-600">Failed</div>
                  </div>
                  <div className="al-text-center al-p-4 al-bg-white al-rounded-lg al-shadow-sm">
                    <div
                      className={`al-text-2xl al-font-bold ${
                        avgScore >= 4
                          ? "al-text-green-600"
                          : avgScore >= 3
                            ? "al-text-yellow-600"
                            : "al-text-red-600"
                      }`}
                    >
                      {avgScore.toFixed(1)}/5
                    </div>
                    <div className="al-text-sm al-text-gray-600">Avg Score</div>
                  </div>
                </div>

                {/* Results table */}
                <div className="al-bg-white al-border al-border-gray-200 al-rounded-md al-overflow-hidden">
                  <table className="al-w-full al-divide-y al-divide-gray-200 al-table-fixed">
                    <colgroup>
                      <col className="al-w-[60%]" />
                      <col className="al-w-[20%]" />
                      <col className="al-w-[20%]" />
                    </colgroup>
                    <thead className="al-bg-gray-50">
                      <tr>
                        <th className="al-px-4 al-py-3 al-text-left al-text-[13px] al-font-normal al-text-gray-500">
                          Test Name
                        </th>
                        <th className="al-px-4 al-py-3 al-text-left al-text-[13px] al-font-normal al-text-gray-500">
                          Score
                        </th>
                        <th className="al-px-4 al-py-3 al-text-left al-text-[13px] al-font-normal al-text-gray-500">
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody className="al-bg-white al-divide-y al-divide-gray-200">
                      {results.map(result => {
                        const isResultExpanded = expandedBatchRunResultId === result.id;
                        return (
                          <React.Fragment key={result.id}>
                            <tr
                              className={`hover:al-bg-gray-50 al-cursor-pointer ${isResultExpanded ? "al-bg-blue-50" : ""}`}
                              onClick={e => {
                                e.stopPropagation();
                                const newExpandedResultId = isResultExpanded ? null : result.id;
                                setExpandedBatchRunResultId(newExpandedResultId);
                                if (newExpandedResultId) {
                                  navigate(
                                    `?tab=runs&batch_run=${encodeURIComponent(runName)}&batch_run_result_id=${result.id}`,
                                    { replace: true }
                                  );
                                } else {
                                  navigate(`?tab=runs&batch_run=${encodeURIComponent(runName)}`, {
                                    replace: true,
                                  });
                                }
                              }}
                            >
                              <td className="al-px-4 al-py-3 al-text-[13px] al-text-gray-900 al-font-medium al-break-words">
                                <Stack direction="row" align="center" gap="sm">
                                  <span className="al-text-gray-400 al-text-xs al-flex-shrink-0">
                                    {isResultExpanded ? "▼" : "▶"}
                                  </span>
                                  <span className="al-break-words al-overflow-hidden">
                                    {result.test_case_name}
                                  </span>
                                </Stack>
                              </td>
                              <td className="al-px-4 al-py-3 al-text-[13px]">
                                <Badge
                                  className={
                                    Math.round(result.overall_score) >= 4
                                      ? "!al-bg-green-100 !al-text-green-700 al-border al-border-green-200"
                                      : Math.round(result.overall_score) >= 3
                                        ? "!al-bg-yellow-100 !al-text-yellow-700 al-border al-border-yellow-200"
                                        : "!al-bg-red-100 !al-text-red-700 al-border al-border-red-200"
                                  }
                                >
                                  {Math.round(result.overall_score)}/5
                                </Badge>
                              </td>
                              <td className="al-px-4 al-py-3 al-text-[13px]">
                                {Math.round(result.overall_score) >= 4 ? (
                                  <Badge className="!al-bg-green-100 !al-text-green-700 al-border al-border-green-200">
                                    Passed
                                  </Badge>
                                ) : (
                                  <Badge className="!al-bg-red-100 !al-text-red-700 al-border al-border-red-200">
                                    Failed
                                  </Badge>
                                )}
                              </td>
                            </tr>
                            {isResultExpanded && (
                              <tr>
                                <td colSpan={3} className="al-p-0">
                                  <div className="al-bg-gray-100 al-p-4 al-max-h-96 al-overflow-y-auto al-overflow-x-hidden">
                                    {/* Turn results */}
                                    <div className="al-space-y-3 al-max-w-full">
                                      {result.turn_results?.map(
                                        (turn: TurnResult, index: number) => (
                                          <TurnResultCard
                                            key={index}
                                            turn={turn}
                                            variant="compact"
                                          />
                                        )
                                      )}
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
    </React.Fragment>
  );
};
